import axiosInstance from './AxiosInterceptor';
import { addBreadcrumb, captureException } from './sentry';
import { ApiError } from '../models/ApiError';
import {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  Item,
  CreateItemRequest,
  UpdateItemRequest,
  AddCheckRequest,
  Check,
  GetItemsQueryParams,
  ApiSuccessResponse,
} from '../models/SafetyPortalTypes';

/**
 * Login API call
 * @param data - Login request data
 */
export const login = async (data: LoginRequest): Promise<LoginResponse> => {
  addBreadcrumb('Attempting login', 'api', 'info', { userName: data.userName });
  const response = await axiosInstance.post<ApiSuccessResponse<LoginResponse>>('/auth/login', data);
  addBreadcrumb('Login successful', 'api', 'info', { userName: data.userName });
  return response.data as any;
};

/**
 * Refresh token API call
 * @param data - Refresh token request data
 */
export const refreshToken = async (data: RefreshTokenRequest): Promise<RefreshTokenResponse> => {
  const response = await axiosInstance.post<ApiSuccessResponse<RefreshTokenResponse>>('/auth/refresh', data);
  return response.data as any;
};

/**
 * Logout functionality to clear tokens
 */
export const logout = () => {
  // Clear the tokens from localStorage
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('idToken');
  localStorage.removeItem('userName');

  // Redirect to the login page (full page reload)
  window.location.href = '/login';
};

/**
 * Forgot password API call
 * @param userName - The username of the user
 */
export const forgotPassword = async (userName: string) => {
  const action = 'FORGOT_PASSWORD';
  const response = await axiosInstance.post('/auth/login', { 
    userName,
    action      
  });
  return response.data;
};

/**
 * Resend confirmation code API call
 * @param userName 
 */
export const resendConfirmationCode = async (userName: string) => {
  const action = 'RESEND_CONFIRMATION_CODE';
  const response = await axiosInstance.post('/auth/login', { 
    userName,
    action
  });
  return response.data;
};

/**
 * Confirm forgot password API call
 * @param userName - The username
 * @param confirmationCode - The confirmation code
 * @param newPassword - The new password
 */
export const confirmResetRequest = async (userName: string, confirmationCode: string, newPassword: string) => {
  const action = 'CONFIRM_FORGOT_PASSWORD';
  const response = await axiosInstance.post('/auth/login', {
    userName,
    confirmationCode,
    newPassword,
    action
  });
  return response.data;
};

// Items endpoints

/**
 * Get all items with optional filters
 */
export const getItems = async (params?: GetItemsQueryParams): Promise<Item[]> => {
  try {
    addBreadcrumb('Fetching items', 'api', 'info');
    const response = await axiosInstance.get<ApiSuccessResponse<{ items: Item[] }>>('/items', { params });
    addBreadcrumb('Items fetched successfully', 'api', 'info');
    return response.data.items || [];
  } catch (error) {
    return handleApiError(error, 'getItems');
  }
};

/**
 * Get a single item by ID
 * @param id - The ID (can be MongoDB ObjectId or itemId)
 */
export const getItem = async (id: string): Promise<Item> => {
  try {
    addBreadcrumb('Fetching item', 'api', 'info', { id });
    const response = await axiosInstance.get<ApiSuccessResponse<{ item: Item }>>(`/items/${id}`);
    addBreadcrumb('Item fetched successfully', 'api', 'info');
    return response.data.item;
  } catch (error) {
    return handleApiError(error, 'getItem');
  }
};

/**
 * Create a new item
 */
export const createItem = async (data: CreateItemRequest): Promise<Item> => {
  try {
    addBreadcrumb('Creating item', 'api', 'info', { name: data.name });
    const response = await axiosInstance.post<ApiSuccessResponse<{ item: Item }>>('/items', data);
    addBreadcrumb('Item created successfully', 'api', 'info');
    return response.data.item;
  } catch (error) {
    return handleApiError(error, 'createItem');
  }
};

/**
 * Update an existing item
 */
export const updateItem = async (data: UpdateItemRequest): Promise<Item> => {
  try {
    addBreadcrumb('Updating item', 'api', 'info', { id: data._id || data.itemId });
    const response = await axiosInstance.put<ApiSuccessResponse<{ item: Item }>>('/items', data);
    addBreadcrumb('Item updated successfully', 'api', 'info');
    return response.data.item;
  } catch (error) {
    return handleApiError(error, 'updateItem');
  }
};

/**
 * Delete an item
 * @param id - The ID (can be MongoDB ObjectId or itemId)
 */
export const deleteItem = async (id: string): Promise<void> => {
  try {
    addBreadcrumb('Deleting item', 'api', 'info', { id });
    await axiosInstance.delete<ApiSuccessResponse<{}>>(`/items/${id}`);
    addBreadcrumb('Item deleted successfully', 'api', 'info');
  } catch (error) {
    handleApiError(error, 'deleteItem');
  }
};

/**
 * Add a check to an item
 * @param itemId - The item ID (can be MongoDB ObjectId or itemId)
 * @param data - The check data
 */
export const addCheck = async (itemId: string, data: AddCheckRequest): Promise<{ item: Item; check: Check }> => {
  try {
    addBreadcrumb('Adding check to item', 'api', 'info', { itemId, date: data.date });
    const response = await axiosInstance.post<ApiSuccessResponse<{ item: Item; check: Check }>>(
      `/items/${itemId}/checks`,
      data
    );
    addBreadcrumb('Check added successfully', 'api', 'info');
    return {
      item: response.data.item,
      check: response.data.check,
    };
  } catch (error) {
    return handleApiError(error, 'addCheck');
  }
};

/**
 * Helper function to handle API errors with Sentry logging
 * Errors are already converted to ApiError by the interceptor
 */
const handleApiError = (error: any, context: string): never => {
  const apiError = ApiError.isApiError(error) ? error : ApiError.fromUnknownError(error);
  
  const errorToLog = error instanceof Error ? error : new Error(apiError.message);
  errorToLog.name = `ApiError: ${apiError.type}`;
  
  captureException(errorToLog, {
    tags: {
      source: 'ApiService',
      context,
      errorType: apiError.type,
      statusCode: apiError.statusCode?.toString() || 'unknown',
      requestId: apiError.requestId || 'none',
    },
    extra: {
      apiError: {
        type: apiError.type,
        message: apiError.message,
        details: apiError.details,
        timestamp: apiError.timestamp,
        requestId: apiError.requestId,
        statusCode: apiError.statusCode,
      },
    },
  });
  
  throw apiError;
};

